import React, { useEffect, useState } from 'react';
import { useNavigate } from '../hooks/useNavigate';
import { useSelector } from 'react-redux';
import { Dropdown } from 'antd';
import { UserOutlined, LogoutOutlined, DownOutlined, SwapOutlined, MenuOutlined } from '@ant-design/icons';
import { RootState } from '../redux/store';
import { GET } from '../utils/apiRequest';
import API from '../config/apis';
import ProfileDrawer from '../components/profile/ProfileDrawer';
import { BrandLogo } from '../components/ui';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const toAbsUrl = (path: string | null | undefined) => {
  if (!path) return null;
  return path.startsWith('http') ? path : `${API_BASE}${path}`;
};

interface LayoutHeaderProps {
  user: {
    name: string;
    role: string;
  } | null;
  onLogout?: () => void;
  actions?: React.ReactNode;
  isMobile?: boolean;
  onMenuClick?: () => void;
}

const ROLE_PREFIX: Record<string, string> = {
  SUPER_ADMIN: '/superadmin',
  INSTITUTION_ADMIN: '/admin',
  FEST_ADMIN: '/admin',
  ITEM_COORDINATOR: '/coordinator',
  JUDGE: '/judge',
  VOLUNTEER: '/volunteer',
  PARTICIPANT: '/participant',
};

const LayoutHeader: React.FC<LayoutHeaderProps> = ({ user, onLogout, isMobile, onMenuClick }) => {
  const navigate = useNavigate();
  const { user: storeUser } = useSelector((state: RootState) => state.User);
  const currentFest = useSelector((state: RootState) => state.Fest.currentFest);
  const [institutionLogo, setInstitutionLogo] = useState<string | null>(null);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);

  const rolePrefix = ROLE_PREFIX[storeUser?.role || ''] || '/admin';
  const isFestManage = !!currentFest;
  const isInstitutionAdmin = storeUser?.role === 'INSTITUTION_ADMIN';

  useEffect(() => {
    if (!isInstitutionAdmin) return;
    const instId = (storeUser as any)?.institution_id;
    if (!instId) return;
    GET(`${API.INSTITUTIONS}/${instId}`).then(res => {
      if (res.status && res.data) {
        const inst = (res.data as any).data || res.data;
        if (inst.logo) setInstitutionLogo(inst.logo);
      }
    });
  }, [isInstitutionAdmin, (storeUser as any)?.institution_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const festMenuItems = isFestManage
    ? [
      {
        key: 'switch-fest',
        icon: <SwapOutlined />,
        label: 'Switch Fest',
        onClick: () => navigate(`${rolePrefix}/fests`),
      },
      { type: 'divider' as const },
    ]
    : [];

  const menuItems = [
    ...festMenuItems,
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'My Profile',
      onClick: () => setProfileDrawerOpen(true),
    },
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: <span style={{ color: 'var(--destructive)' }}>Logout</span>,
      onClick: onLogout,
    },
  ];

  const initials = user?.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'U';

  return (
    <>
      <header className="app-header sticky top-0 z-10 flex h-[60px] items-center justify-between border-b border-border bg-card px-6 max-md:px-3">
        {/* Hamburger + logo for mobile, spacer for desktop */}
        {isMobile ? (
          <div className="flex items-center gap-2">
            <button
              onClick={onMenuClick}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-accent"
            >
              <MenuOutlined style={{ fontSize: 18 }} />
            </button>
            <BrandLogo variant="icon" height={24} />
          </div>
        ) : <div />}
        <Dropdown
          menu={{ items: menuItems }}
          trigger={['click']}
          placement="bottomRight"
        >
          <div className="flex items-center gap-3 cursor-pointer rounded-lg px-2 py-1 transition-colors hover:bg-accent select-none">
            {isInstitutionAdmin && institutionLogo ? (
              <div className="flex h-9 w-9 items-center justify-center rounded-lg overflow-hidden border border-border bg-muted flex-shrink-0">
                <img src={toAbsUrl(institutionLogo)!} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }} />
              </div>
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground flex-shrink-0">
                {initials}
              </div>
            )}
            <div className="hidden sm:block text-right">
              <div className="text-[13px] font-medium leading-[18px] text-foreground">
                {user?.name}
              </div>
              <div className="text-[11px] leading-[14px] text-muted-foreground capitalize">
                {storeUser?.role === 'CUSTOM' && (storeUser as any)?.roleName
                  ? (storeUser as any).roleName
                  : user?.role?.replace(/_/g, ' ').toLowerCase()}
              </div>
            </div>
            <DownOutlined className="text-[10px] text-muted-foreground hidden sm:block" />
          </div>
        </Dropdown>
      </header>

      <ProfileDrawer open={profileDrawerOpen} onClose={() => setProfileDrawerOpen(false)} />
    </>
  );
};

export default React.memo(LayoutHeader);
